import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Settings, Send, Eye, EyeOff, Save, Users, CheckSquare, Square } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SmsPage = () => {
  const { language } = useLanguage();

  const [smsApiKey, setSmsApiKey] = useState('');
  const [smsNodeUrl, setSmsNodeUrl] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);

  const [users, setUsers] = useState([]);
  const [selectedPhones, setSelectedPhones] = useState(new Set());
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');
  // ponytail: non-ASCII (diacritics, emoji) => UCS-2 (70/67 per part); ignores GSM extension chars like [ ] { } that count double
  const [single, multi] = /[^\x00-\x7F]/.test(message) ? [70, 67] : [160, 153];
  const smsParts = message.length <= single ? 1 : Math.ceil(message.length / multi);
  const [sending, setSending] = useState(false);
  const [sendResults, setSendResults] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/app-settings`)
      .then(res => {
        setSmsApiKey(res.data.sms_node_api_key || '');
        setSmsNodeUrl(res.data.sms_node_url || '');
      })
      .catch(e => console.error(e));

    axios.get(`${API}/admin/users`)
      .then(res => setUsers(res.data.filter(u => u.email !== 'admin@ecowash.ro' && u.email !== 'operator@ecowash.ro')))
      .catch(e => console.error(e));
  }, []);

  const handleSaveConfig = async () => {
    setConfigSaving(true);
    setConfigSaved(false);
    try {
      const res = await axios.get(`${API}/admin/app-settings`);
      await axios.put(`${API}/admin/app-settings`, {
        ...res.data,
        sms_node_api_key: smsApiKey,
        sms_node_url: smsNodeUrl,
      });
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 2000);
    } catch (e) {
      console.error(e);
    } finally {
      setConfigSaving(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const q = search.toLowerCase();
    return (
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
      u.phone?.toLowerCase().includes(q) ||
      u.company_name?.toLowerCase().includes(q)
    );
  });

  const allSelected = filteredUsers.length > 0 && filteredUsers.every(u => selectedPhones.has(u.phone));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedPhones(prev => {
        const next = new Set(prev);
        filteredUsers.forEach(u => next.delete(u.phone));
        return next;
      });
    } else {
      setSelectedPhones(prev => {
        const next = new Set(prev);
        filteredUsers.forEach(u => { if (u.phone) next.add(u.phone); });
        return next;
      });
    }
  };

  const toggleUser = (phone) => {
    setSelectedPhones(prev => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone);
      else next.add(phone);
      return next;
    });
  };

  const handleSend = async () => {
    if (!message.trim() || selectedPhones.size === 0) return;
    setSending(true);
    setSendResults(null);
    try {
      const res = await axios.post(`${API}/admin/sms/send`, {
        message: message.trim(),
        phones: Array.from(selectedPhones),
      });
      setSendResults(res.data.results);
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  const ro = language === 'ro';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
          {ro ? 'Trimitere SMS' : 'Send SMS'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {ro ? 'Trimite SMS-uri utilizatorilor prin SMS Node App' : 'Send SMS messages to users via SMS Node App'}
        </p>
      </div>

      {/* Config */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <Settings className="w-4 h-4 text-primary" />
            {ro ? 'Configurare SMS Node App' : 'SMS Node App Configuration'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3">
            <Input
              placeholder="URL (ex: https://webworks24.ro/sms-node-app-api)"
              value={smsNodeUrl}
              onChange={e => setSmsNodeUrl(e.target.value)}
              className="flex-1"
            />
            <div className="flex-1 relative">
              <Input
                type={showApiKey ? 'text' : 'password'}
                placeholder={ro ? 'API Key' : 'API Key'}
                value={smsApiKey}
                onChange={e => setSmsApiKey(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Button onClick={handleSaveConfig} disabled={configSaving} size="sm" className="gap-2 shrink-0">
              <Save className="w-4 h-4" />
              {configSaved ? (ro ? 'Salvat!' : 'Saved!') : (ro ? 'Salvează' : 'Save')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Compose + Recipients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Message */}
        <Card className="flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <Send className="w-4 h-4 text-primary" />
              {ro ? 'Mesaj' : 'Message'}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 flex-1">
            <textarea
              className="w-full flex-1 min-h-[200px] rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={ro ? 'Scrie mesajul SMS...' : 'Write your SMS message...'}
              value={message}
              onChange={e => setMessage(e.target.value)}
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{message.length} {ro ? 'caractere' : 'chars'} · {smsParts} SMS</span>
              <Button
                onClick={handleSend}
                disabled={sending || !message.trim() || selectedPhones.size === 0}
                className="gap-2"
              >
                <Send className="w-4 h-4" />
                {sending
                  ? (ro ? 'Se trimite...' : 'Sending...')
                  : `${ro ? 'Trimite' : 'Send'} (${selectedPhones.size})`}
              </Button>
            </div>

            {sendResults && (
              <div className="space-y-1 mt-2 max-h-40 overflow-y-auto">
                {sendResults.map((r, i) => (
                  <div key={i} className={`text-xs px-2 py-1 rounded flex justify-between ${r.ok ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}>
                    <span>{r.phone}</span>
                    <span>{r.ok ? '✓' : `✗ ${r.error || r.status}`}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recipients */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <Users className="w-4 h-4 text-primary" />
              {ro ? 'Destinatari' : 'Recipients'}
              <span className="ml-auto text-xs font-normal text-muted-foreground">
                {selectedPhones.size} {ro ? 'selectați' : 'selected'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder={ro ? 'Caută utilizator...' : 'Search user...'}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <div
              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer select-none border-b pb-2"
              onClick={toggleAll}
            >
              {allSelected
                ? <CheckSquare className="w-4 h-4 text-primary shrink-0" />
                : <Square className="w-4 h-4 text-muted-foreground shrink-0" />}
              <span className="text-sm font-medium">{ro ? 'Selectează toți' : 'Select all'}</span>
            </div>
            <div className="space-y-1 max-h-[280px] overflow-y-auto">
              {filteredUsers.map(u => (
                <div
                  key={u.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer select-none"
                  onClick={() => u.phone && toggleUser(u.phone)}
                >
                  {selectedPhones.has(u.phone)
                    ? <CheckSquare className="w-4 h-4 text-primary shrink-0" />
                    : <Square className="w-4 h-4 text-muted-foreground shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{u.first_name} {u.last_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.phone}{u.company_name ? ` · ${u.company_name}` : ''}</p>
                  </div>
                </div>
              ))}
              {filteredUsers.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {ro ? 'Niciun utilizator găsit' : 'No users found'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SmsPage;
